import { useState } from 'react';
import { Loader2, Check } from 'lucide-react';

interface TwoFactorSetupProps {
  qrCodeUrl?: string;
  secretKey?: string;
  onActivate: (code: string) => Promise<boolean>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export function TwoFactorSetup({
  qrCodeUrl,
  secretKey,
  onActivate,
  onCancel,
  isLoading = false,
  error = null
}: TwoFactorSetupProps) {
  const [copied, setCopied] = useState(false);

  const handleCopySecret = async () => {
    if (secretKey) {
      try {
        await navigator.clipboard.writeText(secretKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = secretKey;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleActivate = async () => {
    // For now, just call onActivate with an empty string since we removed the verification code input
    await onActivate('');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/img/logo.png" 
              alt="LogisHaus" 
              className="h-12 mr-3"
            />
            <span className="text-2xl font-bold text-gray-900">LogisHaus</span>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900">Two - factor authentication</h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* QR Code */}
        <div className="mb-6 text-center">
          {qrCodeUrl ? (
            <div className="space-y-4">
              <div className="inline-block">
                <img
                  src={qrCodeUrl}
                  alt="2FA QR Code"
                  className="w-48 h-48"
                />
              </div>
            </div>
          ) : (
            <div className="w-48 h-48 mx-auto bg-gray-100 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          )}
        </div>

        {/* Instructions Text */}
        <div className="mb-6 text-center">
          <p className="text-sm text-gray-600">
            "Scan the QR Code using any authentication application on your phone (e.g. <a href="https://support.google.com/accounts/answer/1066447" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Google Authenticator</a>, <a href="https://duo.com/product/trusted-users/two-factor-authentication/duo-mobile" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Duo Mobile</a>, <a href="https://authy.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Authy</a>) or enter the following code:
          </p>
        </div>

        {/* Manual Entry Key */}
        {secretKey && (
          <div className="mb-6 text-center">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-sm font-mono text-gray-800 select-all">
                {secretKey}
              </span>
              <button
                onClick={handleCopySecret}
                className="p-1 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <img src="/img/ion_copy-sharp.svg" alt="Copy" className="w-5 h-5" />
                )}
              </button>
            </div>
            {copied && (
              <p className="text-xs text-green-600 mt-1">Copied to clipboard!</p>
            )}
          </div>
        )}

        {/* Separator */}
        <hr className="border-gray-200 mb-6" />

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 focus:outline-none transition-colors disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleActivate}
            disabled={isLoading}
            className="flex-1 bg-yellow-400 text-black py-3 px-4 rounded-lg font-medium hover:bg-yellow-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Activating...
              </div>
            ) : (
              'Activate'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
